/**
 * PlanGenerator — the algorithm interface for the Plan Library.
 *
 * A PlanGenerator is a pure, read-only algorithm that converts raw learning
 * data + a SavedPlan configuration into a CoachPlan. All FSRS writes happen
 * exclusively in the session runner via applyReview(); generators never write
 * card state. This invariant is what makes FSRS progress portable across plans.
 */

import type { Item, CurriculumNode } from "./item-library.js";
import type { CardStore, MasteryThresholds } from "./scheduler.js";
import type { ReviewSummary } from "./skill-state.js";
import type { CoachPlan, SessionMix, PlanConfig } from "./plan-template.js";

// ─── PlanDependencies ─────────────────────────────────────────────────────────

/** Raw learning data assembled by the hub before calling any generator. */
export interface PlanDependencies {
  items: Item[];
  allNodes: CurriculumNode[];
  cards: CardStore;
  thresholds: MasteryThresholds;
  reviews: readonly ReviewSummary[];
  nodeExposures: ReadonlyMap<string, number>;
  /**
   * Tester override — forwarded to the acquisition gate so introduced nodes
   * are treated as "ready" (no Listen-first / bypass-quiz). Driven by the
   * `unlockEverything` pref. Optional; defaults to false.
   */
  forceAcquired?: boolean;
  now: Date;
}

// ─── SavedPlan ────────────────────────────────────────────────────────────────

/** A serializable plan record. App plans live in APP_PLANS; user plans in the DB. */
export interface SavedPlan {
  /** "app-standard" | "user-<uuid>" */
  id: string;
  name: string;
  source: "app" | "user";
  /** Key into PLAN_GENERATORS registry. */
  generatorId: string;
  sessionMix: SessionMix;
  /** null = all tracks in scope */
  focusTracks: string[] | null;
  /** Overrides focusTracks when set (advanced). */
  focusNodeIds: string[] | null;
  configOverrides: Partial<PlanConfig> | null;
  /** Generator-specific scheduling state (Option A). null for FSRS generators. */
  generatorState: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

// ─── PlanGenerator ────────────────────────────────────────────────────────────

/**
 * Pure read — never writes card state.
 * All FSRS writes happen exclusively in the session runner via applyReview().
 * Any two plans covering the same node share FSRS state because neither
 * generator can corrupt the other's scheduling.
 */
export interface PlanGenerator {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  build(deps: PlanDependencies, plan: SavedPlan, sessionMinutes: number): CoachPlan;
}

// ─── resolveNodes ─────────────────────────────────────────────────────────────

/**
 * Apply focusNodeIds / focusTracks filtering from a SavedPlan to a template's
 * node list. Returns the full list when both fields are null.
 */
/**
 * Count cards that are overdue and belong to nodes outside the active plan's
 * scope. Returns 0 when the plan covers all tracks (focusTracks === null and
 * focusNodeIds === null), so the callout never fires for unrestricted plans.
 */
export function countOverdueOutsidePlan(
  plan: SavedPlan,
  cards: CardStore,
  allNodes: readonly CurriculumNode[],
  now: Date = new Date(),
): number {
  if (plan.focusTracks === null && plan.focusNodeIds === null) return 0;

  const coveredIds = plan.focusNodeIds !== null
    ? new Set(plan.focusNodeIds)
    : new Set(
        allNodes
          .filter((n) => (plan.focusTracks as string[]).includes(n.track))
          .map((n) => n.id),
      );

  const nowIso = now.toISOString();
  let count = 0;
  for (const [itemId, card] of Object.entries(cards)) {
    if (card.state === "new") continue;
    const nodeId = itemId.split("::")[0] ?? itemId;
    if (coveredIds.has(nodeId)) continue;
    if (card.due < nowIso) count++;
  }
  return count;
}

export function resolveNodes(nodes: CurriculumNode[], plan: SavedPlan): CurriculumNode[] {
  if (plan.focusNodeIds !== null) {
    const ids = new Set(plan.focusNodeIds);
    return nodes.filter((n) => ids.has(n.id));
  }
  if (plan.focusTracks !== null) {
    const tracks = new Set(plan.focusTracks);
    return nodes.filter((n) => tracks.has(n.track));
  }
  return nodes;
}
